import * as React from 'react';
import {
    getManualAccess,
    ManualAccessPropertyConfiguration,
    addManualAccess,
    revokeManualAccesses,
    disableManualAccess,
    updateManualConfiguration
} from '../../../../../api/PropertyIntegration';
import Button from '../../../../common/form/Button';
import { useState } from 'react';
import Modal from 'reactstrap/lib/Modal';
import LoadingOverlay from '../../../../common/LoadingOverlay';
import FormValidationDisplay from '../../../../common/form/ValidationDisplay';
import ModalBody from 'reactstrap/lib/ModalBody';
import Container from 'reactstrap/lib/Container';
import ManualAccessBackupForm from './manualAccessControls/ManualAccessBackupForm';
import ManualAccessDisplay from './manualAccessControls/ManualAccessDisplay';
import ModalHeader from 'reactstrap/lib/ModalHeader';
import { ApiResult } from '../../../../../api/Shared';
import { userIsCompanyManageUser, userIsPropertyManageUser, userIsSystemManageUser } from '../../../../../utilities/identity';

interface Props {
    propertyId: number;
    manualCodesEnabled: boolean;
    updateManualCodesEnabled: (enabled: boolean) => void;
    setPageError: (error?: string) => void;
    setPageLoading: (isLoading: boolean) => void;
}

const ManualAccessConfigurationSection = (props: Props) => {
    const isUser =
    userIsCompanyManageUser() ||
    userIsPropertyManageUser() ||
    userIsSystemManageUser();
    const [modalOpen, setModalOpen] = useState(false);
    const [loading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);
    const [codeConfig, setCodeConfig] = useState<ManualAccessPropertyConfiguration | undefined>(
        undefined
    );

    const load = async () => {
        setModalOpen(true);
        setIsLoading(true);

        let configResult = await getManualAccess(props.propertyId);
        if (configResult.success && configResult.data) {
            setCodeConfig(configResult.data);
        } else if (configResult.error) {
            setError(configResult.error);
        } else {
            setCodeConfig({});
        }

        setIsLoading(false);
    };

    const close = () => {
        setModalOpen(false);
        setError(undefined);
    };

    const manualCodeDataRequest = async (
        request: Promise<ApiResult<ManualAccessPropertyConfiguration>>,
        errorMessage?: string
    ) => {
        setIsLoading(true);
        let result = await request;
        if (result.success && result.data) {
            setCodeConfig(result.data);
            setError(undefined);
        } else {
            setError(result.error || errorMessage || 'Request Failed');
        }
        props.updateManualCodesEnabled(true);
        setIsLoading(false);
    };

    const updateBackupCode = (backupCode: string, name: string) =>
        manualCodeDataRequest(
            updateManualConfiguration(props.propertyId, backupCode, name),
            'Failed to Update Configuration'
        );

    const saveCode = (code: string, name: string) =>
        manualCodeDataRequest(addManualAccess(props.propertyId, code, name), 'Failed to Save Code');

    const deleteCode = (codeIds: number[]) =>
        manualCodeDataRequest(
            revokeManualAccesses(props.propertyId, codeIds),
            'Failed to Delete Code'
        );

    const disableIntegration = async () => {
        setModalOpen(false);
        props.setPageLoading(true);
        let result = await disableManualAccess(props.propertyId);
        if (result.success) {
            props.updateManualCodesEnabled(false);
        } else {
            props.setPageError(result.error || 'Failed to disable integration');
        }
        props.setPageLoading(false);
    };

    return (
        <div className="access-integration-buttons">
            {props.manualCodesEnabled ? (
                <>
                    <Button text="Edit Manual Codes" onClick={load} disabled={isUser} />
                    <Button
                        text="Deactivate Manual Code Access"
                        onClick={disableIntegration}
                        addlClassName="btn-danger integration-second-button"
                        disabled={isUser}
                    />
                </>
            ) : (
                <Button text="Enable Manual Codes" onClick={load} disabled={isUser}/>
            )}
            <Modal size="lg" isOpen={modalOpen} toggle={close} className="manual-code-modal">
                <LoadingOverlay show={loading} inBootstrapColumn={false} />
                <ModalHeader toggle={close}>Entry Codes</ModalHeader>
                <ModalBody>
                    {!loading && (
                        <Container>
                            <FormValidationDisplay errorMessage={error} />
                            <ManualAccessBackupForm
                                initialBackupCode={codeConfig && codeConfig.backupCode}
                                name={codeConfig && codeConfig.name}
                                onBackupCodeSave={
                                    codeConfig && codeConfig.backupCode
                                        ? updateBackupCode
                                        : saveCode
                                }
                            />
                            {codeConfig && codeConfig.codes && (
                                <ManualAccessDisplay
                                    deleteCodes={deleteCode}
                                    addCode={saveCode}
                                    codes={codeConfig.codes}
                                />
                            )}
                        </Container>
                    )}
                </ModalBody>
            </Modal>
        </div>
    );
};

export default ManualAccessConfigurationSection;



// WEBPACK FOOTER //
// ./src/components/pages/admin/editProperty/integrationControls/ManualAccessConfigurationSection.tsx